/**
 * Adapted Chart component.
 *
 * Charts always require inputDataset for data lookup - they cannot use inline data.
 * Uses useToolOutputData to get raw tool output, then transforms via chart adapter helpers.
 */

import { useAuiState } from '@assistant-ui/react';

import { Chart } from '#core/components/dashboard/panels/chart/Chart.component.tsx';
import type { ChartSeriesInput, ChartType } from '#core/components/dashboard/panels/chart/schema.ts';
import { getComponentSkeleton } from '#core/components/dashboard/registry.ts';

import type { ChatDashboardItem } from '#plugin-chat/components/dashboard/types.ts';
import { BlockError } from '#plugin-chat/components/shared/BlockError.tsx';
import type { ChartAdapterOutput } from '#plugin-chat/components/tools/show-dashboard/helpers.ts';
import {
  createChartAdapter,
  createTimeseriesChartAdapter,
  extractLlmSeries,
  resolveChartOutput,
} from '#plugin-chat/components/tools/show-dashboard/helpers.ts';
import { useDataset, useDatasets } from '#plugin-chat/hooks/useDataset.ts';
import type { Dataset } from '#plugin-chat/lib/data-pipeline/types.ts';

function transformChartData(dataset: Dataset, toolName: string | undefined): ChartAdapterOutput | undefined {
  if (toolName === 'getDatapoints') {
    return createTimeseriesChartAdapter(dataset);
  }
  return createChartAdapter(dataset);
}

export interface AdaptedChartProps {
  item: ChatDashboardItem;
}

export function AdaptedChart({ item }: AdaptedChartProps) {
  const isThreadRunning = useAuiState(({ thread }) => thread.isRunning);
  const isMessageRunning = useAuiState(({ part }) => part.status.type === 'running');
  const toolName = useAuiState(({ part }) => (part.type === 'tool-call' ? part.toolName : undefined));

  // LLM-shape detection: charts drive their data via per-series `inputDataset`.
  // Any other shape falls through to the classic single-dataset path
  // driven by item-level `inputDataset`.
  const llmSeries = extractLlmSeries(item.component);
  const inputDatasets = [...new Set(llmSeries.map((s) => s.inputDataset))];

  // Hook order is stable across renders because both paths always call both hooks.
  const singleDataset = useDataset(item.inputDataset);
  const multiDatasets = useDatasets(inputDatasets);

  const output = resolveChartOutput({
    llmSeries,
    multiDatasets,
    singleDataset,
    toolName,
    component: item.component,
    transformSingleDataset: transformChartData,
  });

  if ((isThreadRunning && !output?.data) || isMessageRunning) {
    const Skeleton = getComponentSkeleton('chart');
    if (!Skeleton) {
      return null;
    }
    return <Skeleton />;
  }

  if (output?.error) {
    return <BlockError blockType="Chart" message={output.error} />;
  }

  if (!output?.data) {
    return (
      <BlockError
        blockType="Chart"
        message="Chart requires either item-level inputDataset or per-series inputDataset"
      />
    );
  }

  // Spread all LLM-specified chart props, override with adapter-resolved data,
  // axes, and series (the merged path rewrites series to qualified column names).
  return (
    <Chart
      {...item.component}
      chartType={(item.component.chartType as ChartType | undefined) ?? output.chartType ?? 'line'}
      data={output.data as Record<string, unknown>[]}
      series={output.series ?? (item.component.series as ChartSeriesInput[] | undefined)}
      title={item.caption ?? (item.component.title as string | undefined)}
      xAxis={(item.component.xAxis as string | undefined) ?? output.xAxis}
      yAxis={(item.component.yAxis as string | undefined) ?? output.yAxis}
    />
  );
}
